import { useMemo, useState, useCallback } from 'react';
import type { Bot, StrategyGroup, StrategyGroupName } from '@/types/bot';
import { useLiveBots } from '@/hooks/useLiveBots';
import { useMarketData } from '@/hooks/useMarketData';
import { useForexRates } from '@/contexts/ForexRatesContext';
import TopBar from '@/components/layout/TopBar';
import { LeftPanel } from '@/components/panels/LeftPanel';
import { CenterPanel } from '@/components/panels/CenterPanel';
import { RightPanel } from '@/components/panels/RightPanel';
import { MfaDialog } from '@/components/shared/MfaDialog';
import './LiveDashboard.css';

function groupBotsByStrategy(bots: Bot[]): StrategyGroup[] {
  const groups = new Map<StrategyGroupName, Bot[]>();
  for (const bot of bots) {
    const existing = groups.get(bot.strategyGroup) || [];
    existing.push(bot);
    groups.set(bot.strategyGroup, existing);
  }
  return Array.from(groups.entries()).map(([name, groupBots]) => ({
    name,
    status: groupBots.some(b => b.status === 'active') ? 'active' as const
      : groupBots.every(b => b.status === 'stopped') ? 'stopped' as const
      : 'paused' as const,
    bots: groupBots,
  }));
}

function computeTotalPnlAud(bots: Bot[], rates: Record<string, number>): number {
  return bots.reduce((sum, bot) => {
    const pnl = bot.performance?.todayPnl || 0;
    const rate = rates[bot.market] || 1;
    return sum + pnl * rate;
  }, 0);
}

export default function LiveDashboard() {
  const { bots, startBot, stopBot } = useLiveBots();
  const { indices, infraHealth } = useMarketData();
  const { rates } = useForexRates();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mfaBot, setMfaBot] = useState<{ id: string; name: string } | null>(null);

  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const handleStartBot = useCallback((botId: string) => {
    const bot = bots.find(b => b.id === botId);
    if (bot && bot.broker === 'prostocks') {
      setMfaBot({ id: bot.id, name: bot.name });
    } else {
      startBot(botId);
    }
  }, [bots, startBot]);

  const totalPnlAud = useMemo(() => computeTotalPnlAud(bots, rates), [bots, rates]);
  const strategyGroups = useMemo(() => groupBotsByStrategy(bots), [bots]);
  // Sync is OK if REST API works (infraHealth fetched successfully), regardless of WebSocket
  const syncStatus = infraHealth.syncStatus;

  return (
    <div className="live-dashboard">
      <TopBar
        totalPnlAud={totalPnlAud}
        syncStatus={syncStatus}
        indices={indices}
        onHamburgerClick={toggleSidebar}
      />
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'sidebar-overlay--visible' : ''}`}
        onClick={closeSidebar}
      />
      <div className="dashboard-panels">
        <LeftPanel
          infraHealth={{ ...infraHealth, syncStatus }}
          strategyGroups={strategyGroups}
          onStartBot={handleStartBot}
          onStopBot={stopBot}
          isOpen={sidebarOpen}
        />
        <CenterPanel bots={bots} />
        <RightPanel />
      </div>

      {mfaBot && (
        <MfaDialog
          botName={mfaBot.name}
          onSubmit={(otp) => { startBot(mfaBot.id, otp); setMfaBot(null); }}
          onCancel={() => setMfaBot(null)}
        />
      )}
    </div>
  );
}
